import { yupResolver } from '@hookform/resolvers/yup';
import { SimpleGrid } from '@mantine/core';
import {
  BonusPeriodModel,
  PreviousBonusPeriodEmployeesModel,
} from 'api-hooks/bonus';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { Form, FormState, Input } from 'components/elements';
import AfterTodayDatePicker from 'components/elements/after-today-date-picker';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import {
  EmptyTabStyles,
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import EmployeeTableInput from './employee-table-input';
import { BonusPeriodFormSchema, BonusPeriodFormType } from './form-type';
import JobLevelTableInput from './job-level-table-input';
import PerformanceTableInput from './performance-table-input';
import SaleConfigurationTableInput from './sale-configuration-table-input';
import useGetDefaultValues from '../hooks/use-get-default-values';

export interface BonusPeriodFormProps {
  bonusPeriod?: BonusPeriodModel;
  previousEmployees?: PreviousBonusPeriodEmployeesModel[];
  onSubmit: (values: BonusPeriodFormType) => Promise<BonusPeriodModel>;
}
enum TabNavigationEnum {
  Sales = 'sales',
  NonSales = 'non-sales',
}

export default function BonusPeriodForm(props: BonusPeriodFormProps) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { bonusPeriod, previousEmployees } = props;
  const defaultValues = useGetDefaultValues(bonusPeriod, previousEmployees);
  const resolver = yupResolver(BonusPeriodFormSchema());
  const methods = useForm({
    defaultValues,
    resolver,
  });
  const onSubmit = React.useCallback(
    async (values: BonusPeriodFormType) => {
      try {
        await props.onSubmit(values);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods.setError, props],
  );

  const customViewName =
    bonusPeriod &&
    [
      t('common:edit_extra', { extra: t('common:bonus-period') }),
      t(`common:bonus-period`),
    ].join(' - ');

  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'bonus-periods',
    bonusPeriod?.id,
  );

  const permission = useGetCrudPermission('bonus-periods');

  const [tab, setTab] = React.useState(TabNavigationEnum.Sales);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!bonusPeriod}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => {
          return (
            <>
              <FormHeader
                data={bonusPeriod}
                onDelete={onDelete}
                isDeleteLoading={isDeleteLoading}
                noDelete={!permission.delete || editable}
                noEdit={!permission.update}
                title={
                  bonusPeriod
                    ? customViewName ??
                      t('common:edit_extra', {
                        extra: t('common:bonus-period'),
                      })
                    : t('common:add_extra', { extra: t('common:bonus-period') })
                }
              />
            </>
          );
        }}
      </FormState>
      <FullContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="name"
              label={t('common:name')}
              placeholder={t('common:name')}
              required
              onAfterChange={() => {
                methods.clearErrors();
              }}
            />
          </HalfContainer>
          <HalfContainer>
            <AfterTodayDatePicker
              name="issueAt"
              label={t('common:issue_at')}
              onAfterChange={() => {
                methods.clearErrors();
              }}
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="startAt"
              label={t('common:started_at')}
              placeholder={t('common:started_at')}
              required
              onAfterChange={() => {
                methods.clearErrors();
              }}
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="endAt"
              label={t('common:ended_at')}
              placeholder={t('common:ended_at')}
              required
              onAfterChange={() => {
                methods.clearErrors();
              }}
            />
          </HalfContainer>
        </SectionContainer>
        <Tabs
          activeKey={tab}
          items={[
            {
              key: TabNavigationEnum.Sales,
              component: <></>,
              title: t('enum:sales'),
            },
            {
              key: TabNavigationEnum.NonSales,
              component: <></>,
              title: t('enum:non-sales'),
            },
          ]}
          onChangeActive={({ activeKey }) => {
            setTab(activeKey);
          }}
          overrideStyles={EmptyTabStyles}
        />
        <div
          style={{
            display: tab === TabNavigationEnum.Sales ? undefined : 'none',
          }}
        >
          <HalfContainer key={TabNavigationEnum.Sales}>
            <SaleConfigurationTableInput />
          </HalfContainer>
        </div>

        <div
          style={{
            display: tab === TabNavigationEnum.NonSales ? undefined : 'none',
          }}
        >
          <HalfContainer key={TabNavigationEnum.NonSales}>
            <Input
              type="number"
              name="profitPercentage"
              label={t('common:bonus_profit')}
              placeholder={t('common:bonus_profit')}
              endEnhancer="%"
              required
            />
          </HalfContainer>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl">
            <PerformanceTableInput />
            <JobLevelTableInput />
          </SimpleGrid>
          <Separator gap={24} />
          <EmployeeTableInput />
        </div>
        <Separator gap={256} />
      </FullContainer>
    </Form>
  );
}
